import { apiClient, APIResponse, apiPathFromAbsoluteUrl, extractApiError, buildQuery } from './api';

/**
 * Expenses + categories API wrappers.
 *
 * All responses follow the project-wide `APIResponse` envelope. Cursor
 * pagination for the expenses list comes through unchanged — callers should
 * treat `data.results` + `data.next` / `data.previous`.
 */

/** DRF list may be a plain array or a paginated envelope. */
export function normalizeCategoryList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

export const expensesAPI = {
  // ----- Categories ------------------------------------------------------
  async listCategories(params) {
    try {
      const { data } = await apiClient.get(`/categories/${buildQuery(params)}`);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar as categorias.'),
      );
    }
  },

  // CHANGE v2 §16: top N categorias mais usadas nos últimos N dias.
  async topCategories(params) {
    try {
      const { data } = await apiClient.get(
        `/categories/top/${buildQuery(params)}`,
      );
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar o top de categorias.'),
      );
    }
  },

  // CHANGE v2 §30.1: categoria sistema única para depósitos em cofres.
  async getSystemPotCategory() {
    try {
      const { data } = await apiClient.get('/categories/system-pot/');
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar a categoria Cofre.'),
      );
    }
  },

  // CHANGE v2 §30.2: reordenar categorias (drag-and-drop).
  async reorderCategories(ids) {
    try {
      const { data } = await apiClient.patch('/categories/reorder/', { ids });
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível reordenar as categorias.'),
      );
    }
  },

  async createCategory(payload) {
    try {
      const { data } = await apiClient.post('/categories/', payload);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível criar a categoria.'),
      );
    }
  },

  async updateCategory(id, payload) {
    try {
      const { data } = await apiClient.patch(`/categories/${id}/`, payload);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível actualizar a categoria.'),
      );
    }
  },

  async archiveCategory(id) {
    try {
      const { data } = await apiClient.post(`/categories/${id}/archive/`);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível arquivar a categoria.'),
      );
    }
  },

  async bulkCreateCategories(items) {
    try {
      const { data } = await apiClient.post('/categories/bulk/', { items });
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível criar as categorias.'),
      );
    }
  },

  async orphanCheck() {
    try {
      const { data } = await apiClient.post('/categories/orphan-check/');
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível verificar categorias órfãs.'),
      );
    }
  },

  // ----- Expenses --------------------------------------------------------
  async listExpenses(params) {
    try {
      const { data } = await apiClient.get(`/expenses/${buildQuery(params)}`);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar as despesas.'),
      );
    }
  },

  async getExpensesPage(url) {
    try {
      const path = apiPathFromAbsoluteUrl(url);
      const { data } = await apiClient.get(path);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível obter mais despesas.'),
      );
    }
  },

  async createExpense(payload) {
    try {
      const { data } = await apiClient.post('/expenses/', payload);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível registar a despesa.'),
      );
    }
  },

  async updateExpense(id, payload) {
    try {
      const { data } = await apiClient.patch(`/expenses/${id}/`, payload);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível actualizar a despesa.'),
      );
    }
  },

  async deleteExpense(id) {
    try {
      await apiClient.delete(`/expenses/${id}/`);
      return APIResponse.success(null);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível apagar a despesa.'),
      );
    }
  },

  async summary(params) {
    try {
      const { data } = await apiClient.get(
        `/expenses/summary/${buildQuery(params)}`,
      );
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar o resumo do mês.'),
      );
    }
  },

  async totals(params) {
    try {
      const { data } = await apiClient.get(
        `/expenses/totals/${buildQuery(params)}`,
      );
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar o total de despesas.'),
      );
    }
  },

  // ----- Recurring + Annual ----------------------------------------------
  async listRecurringTemplates() {
    try {
      const { data } = await apiClient.get('/recurring-templates/');
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar as despesas recorrentes.'),
      );
    }
  },

  async createRecurringTemplate(payload) {
    try {
      const { data } = await apiClient.post('/recurring-templates/', payload);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível criar o template recorrente.'),
      );
    }
  },

  async updateRecurringTemplate(id, payload) {
    try {
      const { data } = await apiClient.patch(`/recurring-templates/${id}/`, payload);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível actualizar o template recorrente.'),
      );
    }
  },

  async deleteRecurringTemplate(id) {
    try {
      await apiClient.delete(`/recurring-templates/${id}/`);
      return APIResponse.success(null);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível apagar o template recorrente.'),
      );
    }
  },

  async launchRecurringNow(id) {
    try {
      const { data } = await apiClient.post(`/recurring-templates/${id}/launch-now/`);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível lançar o template agora.'),
      );
    }
  },

  async listAnnualOccasionals() {
    try {
      const { data } = await apiClient.get('/annual-occasionals/');
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível carregar as despesas anuais.'),
      );
    }
  },

  async createAnnualOccasional(payload) {
    try {
      const { data } = await apiClient.post('/annual-occasionals/', payload);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível criar a despesa anual.'),
      );
    }
  },

  async updateAnnualOccasional(id, payload) {
    try {
      const { data } = await apiClient.patch(`/annual-occasionals/${id}/`, payload);
      return APIResponse.success(data);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível actualizar a despesa anual.'),
      );
    }
  },

  async deleteAnnualOccasional(id) {
    try {
      await apiClient.delete(`/annual-occasionals/${id}/`);
      return APIResponse.success(null);
    } catch (error) {
      return APIResponse.error(
        extractApiError(error, 'Não foi possível apagar a despesa anual.'),
      );
    }
  },
};
